import React, { useState, useEffect } from "react";
import { FeedbackText } from "./FeedbackText";
import type { TUser } from "@/types";

// Strict E.164 phone number validation regex (must start with '+')
const isValidE164 = (phone: string) => {
  if (!phone) return true; // allow empty
  return /^\+[1-9]\d{1,14}$/.test(phone);
};

export interface UserFormProps {
  initialUser?: Partial<TUser>;
  mode: "add" | "edit";
  onSave: (user: TUser) => Promise<void> | void;
  onCancel: () => void;
  loading?: boolean;
  error?: string | null;
  disableEmail?: boolean;
}

export const UserForm: React.FC<UserFormProps> = ({
  initialUser,
  // mode, // not used internally, only for parent logic
  onSave,
  onCancel,
  loading = false,
  error = null,
  disableEmail = false,
}) => {
  const [user, setUser] = useState<TUser>({
    givenName: "",
    familyName: "",
    email: "",
    phoneNumber: "",
    jobTitle: "",
    company: "",
    country: "",
    whatsAppId: "",
    appSource: undefined,
    ...initialUser,
  });
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    setUser((u) => ({ ...u, ...initialUser }));
  }, [initialUser]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (!isValidE164(user.phoneNumber || "")) {
      setLocalError("Phone number must be in E.164 format (e.g. +1234567890)");
      return;
    }
    // Only call onSave if validation passes
    await onSave(user);
  };

  return (
    <form style={{ width: "100%" }} onSubmit={handleSubmit}>
      <div className="form-group">
        <label>Given Name</label>
        <input
          type="text"
          value={user.givenName}
          onChange={(e) => setUser({ ...user, givenName: e.target.value })}
          required
          style={{ width: "100%", minWidth: "320px", boxSizing: "border-box" }}
        />
      </div>
      <div className="form-group">
        <label>Family Name</label>
        <input
          type="text"
          value={user.familyName || ""}
          onChange={(e) => setUser({ ...user, familyName: e.target.value })}
          style={{ width: "100%", minWidth: "320px", boxSizing: "border-box" }}
        />
      </div>
      <div className="form-group">
        <label>Email</label>
        <input
          type="email"
          value={user.email}
          onChange={(e) => setUser({ ...user, email: e.target.value })}
          required
          disabled={disableEmail}
          style={{ width: "100%", minWidth: "320px", boxSizing: "border-box" }}
        />
      </div>
      <div className="form-group">
        <label>Phone Number</label>
        <input
          type="text"
          value={user.phoneNumber || ""}
          onChange={(e) => setUser({ ...user, phoneNumber: e.target.value })}
          style={{ width: "100%", minWidth: "320px", boxSizing: "border-box" }}
        />
        {localError && <FeedbackText message={localError} />}
      </div>
      <div className="form-group">
        <label>Job Title</label>
        <input
          type="text"
          value={user.jobTitle || ""}
          onChange={(e) => setUser({ ...user, jobTitle: e.target.value })}
          style={{ width: "100%", minWidth: "320px", boxSizing: "border-box" }}
        />
      </div>
      <div className="form-group">
        <label>Company</label>
        <input
          type="text"
          value={user.company || ""}
          onChange={(e) => setUser({ ...user, company: e.target.value })}
          style={{ width: "100%", minWidth: "320px", boxSizing: "border-box" }}
        />
      </div>
      <div className="form-group" style={{ marginBottom: "24px" }}>
        <label>Country</label>
        <input
          type="text"
          value={user.country || ""}
          onChange={(e) => setUser({ ...user, country: e.target.value })}
          required
          style={{ width: "100%", minWidth: "320px", boxSizing: "border-box" }}
        />
      </div>
      <div className="modal-actions">
        <button className="modal-btn confirm" type="submit" disabled={loading}>
          {loading ? "Saving..." : "Save"}
        </button>
        <button
          className="modal-btn cancel"
          type="button"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </button>
      </div>
      {error && !localError && <FeedbackText message={error} />}
    </form>
  );
};

export default UserForm;
